import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionApi } from '../services/api';
import QRScanner from '../components/QRScanner';
import { formatSessionCode, isValidSessionCode } from '../utils/helpers';
import './Home.css';

function Home() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [error, setError] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [joining, setJoining] = useState(false);

  // Auto-join when code is complete
  useEffect(() => {
    if (isValidSessionCode(joinCode)) {
      joinSession(joinCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinCode]);

  const handleCreateSession = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await sessionApi.createSession();
      if (response.success) {
        navigate(`/session/${response.sessionId}`, {
          state: { openQRCode: true },
        });
      } else {
        setError('Fehler beim Erstellen der Session');
      }
    } catch (err) {
      console.error('Error creating session:', err);
      setError('Verbindung zum Server fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  // No existence check here: the session page opens the code and re-creates an
  // expired session under the same code, so a typed code, a scanned QR code and
  // a bookmarked URL all behave identically.
  const joinSession = (code) => {
    if (joining) return;

    setJoining(true);
    setError('');
    navigate(`/session/${code}`);
  };

  const handleJoinCodeChange = (e) => {
    const formatted = formatSessionCode(e.target.value);
    setJoinCode(formatted);
    setError('');
  };

  const handleQRScan = (sessionId) => {
    setShowScanner(false);
    navigate(`/session/${sessionId}`);
  };

  return (
    <div className="home">
      <div className="container">
        <div className="home-header">
          <h1 className="home-title">📁 Remote Transfer</h1>
          <p className="home-subtitle">
            Teile Dateien und Texte in Echtzeit – sicher und temporär
          </p>
        </div>

        <div className="home-grid">
          {/* Create Session Card */}
          <div className="card home-card">
            <div className="card-icon">✨</div>
            <h2>Neue Session erstellen</h2>
            <p className="card-description">
              Erstelle eine neue Session und teile den Code oder QR-Code mit anderen
            </p>
            <button
              className="btn btn-primary btn-large"
              onClick={handleCreateSession}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Erstelle Session...
                </>
              ) : (
                <>
                  <span>🚀</span>
                  Session erstellen
                </>
              )}
            </button>
          </div>

          {/* Join Session Card */}
          <div className="card home-card">
            <div className="card-icon">🔗</div>
            <h2>Session beitreten</h2>
            <p className="card-description">
              Tritt einer bestehenden Session bei, indem du den Code eingibst oder den QR-Code scannst
            </p>

            <div className="join-input-group">
              <input
                type="text"
                className="input join-input"
                placeholder="XXXX-XXXX"
                value={joinCode}
                onChange={handleJoinCodeChange}
                maxLength={9}
                disabled={joining}
              />
              {joining && <span className="loading-spinner loading-inline"></span>}
            </div>

            <div className="divider">
              <span>oder</span>
            </div>

            <button
              className="btn btn-primary btn-large"
              onClick={() => setShowScanner(true)}
            >
              <span>📷</span>
              QR-Code scannen
            </button>

            {error && <div className="error-message">{error}</div>}
          </div>
        </div>

        {/* Features Section */}
        <div className="features">
          <h3>Funktionen</h3>
          <div className="features-grid">
            <div className="feature">
              <span className="feature-icon">⚡</span>
              <h4>Echtzeit-Synchronisation</h4>
              <p>Alle Änderungen werden sofort übertragen</p>
            </div>
            <div className="feature">
              <span className="feature-icon">🔒</span>
              <h4>Sicher & Temporär</h4>
              <p>Keine dauerhafte Speicherung deiner Daten</p>
            </div>
            <div className="feature">
              <span className="feature-icon">📱</span>
              <h4>Plattformübergreifend</h4>
              <p>Funktioniert auf allen Geräten</p>
            </div>
            <div className="feature">
              <span className="feature-icon">🎯</span>
              <h4>Einfach & Schnell</h4>
              <p>Keine Registrierung erforderlich</p>
            </div>
          </div>
        </div>
      </div>

      {/* QR Scanner Modal */}
      {showScanner && (
        <QRScanner
          onScan={handleQRScan}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
}

export default Home;
